/**
 * Phase 16 Plan 16-03 — WorkOrderEditPage
 *
 * Route: /work-orders/:id/edit
 * Permission gate: workorders.edit
 *
 * Pre-populated form with guards (D-146):
 *   - WoNumber: editable in Draft and Active; disabled once Completed (client feedback 2026-09)
 *   - SiteId: disabled (shown as site name)
 *   - StartDate / EndDate: editable in Draft and Active; disabled once Completed
 *   - Client + Description: always editable
 *
 * On 409 (WO_DATES_FROZEN / WO_NOT_EDITABLE / WO_NUMBER_FROZEN / WO_NUMBER_DUPLICATE),
 * surfaces the backend error inline.
 */

import { ArrowLeftIcon } from '@phosphor-icons/react';
import { useEffect, useState, type JSX } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useUpdateWorkOrderMutation, useWorkOrderQuery } from '@/hooks/useWorkOrderQueries';
import { useAuth } from '@/lib/auth/AuthContext';
import { useZodForm } from '@/lib/forms/useZodForm';
import { toast } from '@/lib/toast';

const editSchema = z
  .object({
    // Mirrors the create wizard's rule and the backend's ValidateWoNumberFormat
    // (5-30 chars, letters/digits/hyphen/underscore/dot). Enforced against the
    // ORIGINAL value in the refine below rather than here, so a legacy number that
    // predates the format rule cannot block an unrelated description or date edit
    // on the same form.
    woNumber: z.string(),
    originalWoNumber: z.string(),
    description: z.string().max(2000, 'Max 2000 characters').optional(),
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Start date required (yyyy-MM-dd)'),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'End date required (yyyy-MM-dd)'),
  })
  .refine((d) => d.endDate > d.startDate, {
    message: 'End date must be after start date',
    path: ['endDate'],
  })
  .refine(
    (d) => d.woNumber === d.originalWoNumber || /^[A-Za-z0-9._-]{5,30}$/.test(d.woNumber),
    {
      message: 'WO number must be 5-30 chars (letters, digits, . _ -)',
      path: ['woNumber'],
    },
  );

export function WorkOrderEditPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const { permissions } = useAuth();
  const navigate = useNavigate();

  const [serverError, setServerError] = useState<string | null>(null);

  const detailQuery = useWorkOrderQuery(id);
  const updateMutation = useUpdateWorkOrderMutation();

  const form = useZodForm({
    schema: editSchema,
    defaultValues: {
      woNumber: '',
      originalWoNumber: '',
      description: '',
      startDate: '',
      endDate: '',
    },
  });

  const wo = detailQuery.data;

  // Pre-populate form when WO data loads
  useEffect(() => {
    if (wo !== undefined) {
      form.reset({
        woNumber: wo.woNumber,
        originalWoNumber: wo.woNumber,
        description: wo.description ?? '',
        startDate: wo.startDate,
        endDate: wo.endDate,
      });
    }
  }, [wo, form]);

  if (!permissions.includes('workorders.edit')) {
    return <Navigate to="/dashboard" replace />;
  }

  if (detailQuery.isLoading) {
    return (
      <p className="text-muted-foreground text-sm" data-testid="wo-edit-loading">
        Loading work order…
      </p>
    );
  }

  if (detailQuery.isError || wo === undefined) {
    return (
      <div className="text-center py-16" data-testid="wo-edit-not-found">
        <p>Work order not found.</p>
        <Button asChild variant="link">
          <Link to="/work-orders">Back to list</Link>
        </Button>
      </div>
    );
  }

  // Dates stay editable while the WO is running (Draft or Active) so a schedule slip can be
  // recorded. They freeze once it is Completed — reopening is the supported route back.
  const datesDisabled = wo.status !== 'Draft' && wo.status !== 'Active';
  // The WO number follows the same window as the dates: a Completed or Cancelled WO is a
  // historical record whose number may already be printed on issued documents.
  const woNumberDisabled = datesDisabled;
  const clientImmutable = (wo.entryCount ?? 0) > 0;

  const onSubmit = form.handleSubmit((values) => {
    setServerError(null);
    const updateBody: import('@/types/work-orders').UpdateWorkOrderRequest = {
      startDate: values.startDate,
      endDate: values.endDate,
      // Only send woNumber when it actually changed — omitting it means "leave unchanged",
      // which keeps a Completed WO's save working even though its number is frozen.
      ...(values.woNumber !== wo.woNumber ? { woNumber: values.woNumber } : {}),
      ...(typeof values.description === 'string' && values.description.length > 0
        ? { description: values.description }
        : {}),
    };
    updateMutation.mutate(
      { id: wo.id, body: updateBody },
      {
        onSuccess: () => {
          toast.success(`Work Order ${wo.woNumber} updated`);
          void navigate(`/work-orders/${String(wo.id)}`);
        },
        onError: (err) => {
          const msg = err.detail ?? err.message;
          setServerError(msg);
          toast.error('Update failed', { description: msg });
        },
      },
    );
  });

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="mb-2">
          <Link to={`/work-orders/${String(wo.id)}`}>
            <ArrowLeftIcon size={16} /> Back
          </Link>
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">
          Edit Work Order
        </h1>
        <p className="text-muted-foreground font-mono text-sm">{wo.woNumber}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Edit Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} noValidate className="space-y-4">
            {/* WO Number — editable while Draft or Active (client feedback 2026-09) */}
            <div className="space-y-1">
              <Label htmlFor="wo-edit-wonumber">WO Number</Label>
              <Input
                id="wo-edit-wonumber"
                {...form.register('woNumber')}
                disabled={woNumberDisabled}
                data-testid="wo-edit-wonumber-input"
              />
              {form.formState.errors.woNumber !== undefined ? (
                <p className="text-destructive text-xs" data-testid="wo-edit-wonumber-error">
                  {form.formState.errors.woNumber.message}
                </p>
              ) : woNumberDisabled ? (
                <p className="text-xs text-muted-foreground" data-testid="wo-edit-wonumber-frozen-msg">
                  Read-only — the number is fixed once a Work Order is {wo.status}. Reopen it to
                  renumber.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground" data-testid="wo-edit-wonumber-hint">
                  Changing this renumbers the Work Order everywhere in the app. Anything already
                  issued outside it — exported PDFs, emails — keeps the old number, so check with
                  whoever relies on it first. The change is recorded in the audit trail.
                </p>
              )}
            </div>

            {/* Client — read-only (immutable once entries exist) */}
            <div className="space-y-1">
              <Label htmlFor="wo-edit-client">Client</Label>
              <Input
                id="wo-edit-client"
                value={wo.clientName ?? String(wo.clientId ?? '')}
                disabled
                data-testid="wo-edit-client-input"
              />
              {clientImmutable && (
                <p className="text-xs text-muted-foreground" data-testid="wo-edit-client-immutable-msg">
                  Cannot change Client: {String(wo.entryCount ?? 0)} entries reference this WO.
                </p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-1">
              <Label htmlFor="wo-edit-description">Description</Label>
              <Textarea
                id="wo-edit-description"
                rows={3}
                data-testid="wo-edit-description-input"
                {...form.register('description')}
              />
              {form.formState.errors.description !== undefined && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.description.message}
                </p>
              )}
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="wo-edit-start-date">Start Date</Label>
                <Input
                  id="wo-edit-start-date"
                  type="date"
                  disabled={datesDisabled}
                  data-testid="wo-edit-start-date-input"
                  {...form.register('startDate')}
                />
                {form.formState.errors.startDate !== undefined && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.startDate.message}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="wo-edit-end-date">End Date</Label>
                <Input
                  id="wo-edit-end-date"
                  type="date"
                  disabled={datesDisabled}
                  data-testid="wo-edit-end-date-input"
                  {...form.register('endDate')}
                />
                {form.formState.errors.endDate !== undefined && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.endDate.message}
                  </p>
                )}
              </div>
            </div>
            {datesDisabled && (
              <p className="text-xs text-muted-foreground" data-testid="wo-edit-dates-frozen-msg">
                Dates are frozen once a Work Order is completed. Reopen it to change them.
              </p>
            )}

            {/* Server error */}
            {serverError !== null && (
              <div
                className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
                data-testid="wo-edit-server-error"
              >
                {serverError}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button asChild variant="outline">
                <Link to={`/work-orders/${String(wo.id)}`}>Cancel</Link>
              </Button>
              <Button
                type="submit"
                disabled={updateMutation.isPending}
                data-testid="wo-edit-submit-btn"
              >
                {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
